import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { getStatusLabel } from '../lib/proposalUtils';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import PreferenceTable from '../components/PreferenceTable';
import { useTablePreferences } from '../hooks/useTablePreferences';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Phone, 
  Mail, 
  MapPin,
  Building2,
  User,
  Loader2,
  Trash2,
  Edit,
  History,
  X,
  Briefcase,
  FileText,
  LayoutGrid,
  TableIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import StatTile from '../components/StatTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONE_TYPES = ['mobile', 'work', 'home', 'other'];
const EMAIL_TYPES = ['work', 'personal', 'other'];
const ADDRESS_TYPES = ['work', 'home', 'mailing', 'site', 'other'];
const CONTACT_METHODS = ['phone', 'email', 'text'];

export default function PeopleContacts() {
  const { isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreateProposals = isAdmin() || hasPermission('create_proposals');
  const [people, setPeople] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  
  // View mode: 'table' or 'cards' - default to table, persist in localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('contactsViewMode') || 'table';
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('full_name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('contactsViewMode', viewMode);
  }, [viewMode]);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState({ jobs: [], proposals: [], opportunities: [] });
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    company_id: '',
    company_name: '',
    job_title: '',
    department: '',
    phones: [{ id: '', number: '', type: 'mobile', is_primary: true }],
    emails: [{ id: '', email: '', type: 'work', is_primary: true }],
    addresses: [],
    preferred_contact_method: 'phone',
    notes: '',
    tags: []
  });

  // Navigate to proposals with person/company data pre-filled
  const handleNewProposal = async (person) => {
    const params = new URLSearchParams({ new: 'true' });
    
    // Add company ID and name if linked
    if (person.company_id) {
      params.append('companyId', person.company_id);
      
      // Fetch company details to get billing address and AP email
      try {
        const companyResponse = await axios.get(`${API}/contacts/${person.company_id}`);
        const company = companyResponse.data;
        
        if (company) {
          // Use company name
          params.append('company', company.company_name || company.client_name || person.company_name || '');
          
          // Only use BILLING address — leave blank if not set (no fallback to physical)
          if (company.billing_street) params.append('street', company.billing_street);
          if (company.billing_city) params.append('city', company.billing_city);
          if (company.billing_state) params.append('state', company.billing_state);
          if (company.billing_zip) params.append('zip', company.billing_zip);
          
          // Add AP Email from company
          if (company.ar_email_address) {
            params.append('apEmail', company.ar_email_address);
          }
        }
      } catch (error) {
        console.error('Failed to fetch company details:', error);
        // Fallback to just using company name from person
        if (person.company_name) {
          params.append('company', person.company_name);
        }
      }
    } else if (person.company_name) {
      params.append('company', person.company_name);
    }
    
    // Add contact name
    const contactName = person.display_name || `${person.first_name || ''} ${person.last_name || ''}`.trim();
    if (contactName) {
      params.append('contact', contactName);
    }
    
    // Add primary email from contact
    const primaryEmail = person.primary_email || person.emails?.find(e => e.is_primary)?.email || person.emails?.[0]?.email || '';
    if (primaryEmail) {
      params.append('email', primaryEmail);
    }
    
    // Add primary phone from contact
    const primaryPhone = person.primary_phone || person.phones?.find(p => p.is_primary)?.number || person.phones?.[0]?.number || '';
    if (primaryPhone) {
      params.append('phone', primaryPhone);
    }
    
    navigate(`/proposals?${params.toString()}`);
  };

  const fetchPeople = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (companyFilter) params.append('company_id', companyFilter);
      
      const response = await axios.get(`${API}/people?${params}`);
      setPeople(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, companyFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  useEffect(() => {
    fetchPeople();
    fetchCompanies();
  }, [fetchPeople]);

  // Handle ?new=true URL param to open create dialog with company pre-filled
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      const companyId = searchParams.get('companyId');
      const companyName = searchParams.get('companyName');
      
      if (companyId || companyName) {
        setFormData(prev => ({
          ...prev,
          company_id: companyId ? decodeURIComponent(companyId) : '',
          company_name: companyName ? decodeURIComponent(companyName) : ''
        }));
      }
      
      setShowDialog(true);
      setSearchParams({}); // Clear the URL params
    }
  }, [searchParams, setSearchParams]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPeople();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, companyFilter, fetchPeople]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      display_name: '',
      company_id: '',
      company_name: '',
      job_title: '',
      department: '',
      phones: [{ id: '', number: '', type: 'mobile', is_primary: true }],
      emails: [{ id: '', email: '', type: 'work', is_primary: true }],
      addresses: [],
      preferred_contact_method: 'phone',
      notes: '',
      tags: []
    });
    setSelectedPerson(null);
  };

  const handleEdit = (person) => {
    setSelectedPerson(person);
    setFormData({
      first_name: person.first_name || '',
      last_name: person.last_name || '',
      display_name: person.display_name || '',
      company_id: person.company_id || '',
      company_name: person.company_name || '',
      job_title: person.job_title || '',
      department: person.department || '',
      phones: person.phones?.length > 0 ? person.phones : [{ id: '', number: '', type: 'mobile', is_primary: true }],
      emails: person.emails?.length > 0 ? person.emails : [{ id: '', email: '', type: 'work', is_primary: true }],
      addresses: person.addresses || [],
      preferred_contact_method: person.preferred_contact_method || 'phone',
      notes: person.notes || '',
      tags: person.tags || []
    });
    setShowDialog(true);
  };

  const handleViewHistory = async (person) => {
    setSelectedPerson(person);
    try {
      const response = await axios.get(`${API}/people/${person.id}/history`);
      setHistory(response.data);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('First and last name are required');
      return;
    }

    setSaving(true);
    try {
      let companyId = formData.company_id;
      let companyName = formData.company_name;
      
      // If creating a new company, create it first
      if (companyId === '__new__' && companyName?.trim()) {
        try {
          const companyResponse = await axios.post(`${API}/contacts`, {
            company_name: companyName.trim(),
            client_name: companyName.trim(),
            contacts: []
          });
          companyId = companyResponse.data.id;
          // Refresh companies list
          await fetchCompanies();
          toast.success(`Company "${companyName}" created!`);
        } catch (companyError) {
          console.error('Failed to create company:', companyError);
          toast.error('Failed to create company');
          setSaving(false);
          return;
        }
      } else if (companyId === '__new__') {
        // User selected "Create New Company" but didn't enter a name
        companyId = null;
        companyName = null;
      }
      
      // Clean up empty phone/email entries
      const cleanedData = {
        ...formData,
        company_id: companyId || null,
        company_name: companyName || null,
        phones: formData.phones.filter(p => p.number?.trim()),
        emails: formData.emails.filter(e => e.email?.trim()),
        addresses: formData.addresses.filter(a => a.street?.trim() || a.city?.trim())
      };

      if (selectedPerson) {
        await axios.put(`${API}/people/${selectedPerson.id}`, cleanedData);
        toast.success('Contact updated');
      } else {
        await axios.post(`${API}/people`, cleanedData);
        toast.success('Contact created');
      }
      setShowDialog(false);
      resetForm();
      fetchPeople();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save contact';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPerson) return;
    try {
      await axios.delete(`${API}/people/${selectedPerson.id}`);
      toast.success('Contact deleted');
      setShowDeleteDialog(false);
      setSelectedPerson(null);
      fetchPeople();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  // Phone handlers
  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, { id: '', number: '', type: 'mobile', is_primary: false }]
    }));
  };

  const updatePhone = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map((p, i) => {
        if (i === index) {
          if (field === 'is_primary' && value) {
            return { ...p, [field]: value };
          }
          return { ...p, [field]: value };
        }
        if (field === 'is_primary' && value) {
          return { ...p, is_primary: false };
        }
        return p;
      })
    }));
  };

  const removePhone = (index) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index)
    }));
  };

  // Email handlers
  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, { id: '', email: '', type: 'work', is_primary: false }]
    }));
  };

  const updateEmail = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map((e, i) => {
        if (i === index) {
          return { ...e, [field]: value };
        }
        if (field === 'is_primary' && value) {
          return { ...e, is_primary: false };
        }
        return e;
      })
    }));
  };

  const removeEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  // Address handlers
  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, { id: '', street: '', city: '', state: '', zip_code: '', type: 'work', is_primary: false }]
    }));
  };

  const updateAddress = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map((a, i) => {
        if (i === index) {
          return { ...a, [field]: value };
        }
        if (field === 'is_primary' && value) {
          return { ...a, is_primary: false };
        }
        return a;
      })
    }));
  };

  const removeAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }));
  };

  const handleCompanyChange = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      company_name: company?.company_name || company?.client_name || ''
    }));
  };

  const canEdit = isAdmin() || hasPermission('manage_contacts');
  const canDelete = isAdmin() || hasPermission('manage_contacts');

  // Sort function for contacts
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Sorted people
  const sortedPeople = [...people].sort((a, b) => {
    let aVal = '';
    let bVal = '';
    
    if (sortBy === 'full_name') {
      aVal = a.full_name || `${a.first_name || ''} ${a.last_name || ''}`.trim();
      bVal = b.full_name || `${b.first_name || ''} ${b.last_name || ''}`.trim();
    } else if (sortBy === 'company_name') {
      aVal = a.company_name || '';
      bVal = b.company_name || '';
    } else if (sortBy === 'job_title') {
      aVal = a.job_title || '';
      bVal = b.job_title || '';
    } else {
      aVal = a[sortBy] || '';
      bVal = b[sortBy] || '';
    }
    
    // Handle strings
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Column config for PreferenceTable — mirrors the previous hard-coded table.
  const contactColumns = [
    { id: 'full_name', label: 'Name', sortKey: 'full_name', defaultWidth: 220,
      render: (person) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-amber-600" />
          </div>
          <span className="font-medium text-slate-900 truncate">{person.full_name}</span>
        </div>
      ) },
    { id: 'company_name', label: 'Company', sortKey: 'company_name', defaultWidth: 200,
      render: (person) => person.company_name ? (
        <span className="text-slate-600 truncate block">{person.company_name}</span>
      ) : (
        <span className="text-slate-400">—</span>
      ) },
    { id: 'job_title', label: 'Title', sortKey: 'job_title', defaultWidth: 170,
      render: (person) => person.job_title ? (
        <span className="text-slate-600 truncate block">{person.job_title}</span>
      ) : (
        <span className="text-slate-400">—</span>
      ) },
    { id: 'primary_phone', label: 'Phone', defaultWidth: 150,
      render: (person) => person.primary_phone ? (
        <a
          href={`tel:${person.primary_phone}`}
          className="text-amber-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {person.primary_phone}
        </a>
      ) : (
        <span className="text-slate-400">—</span>
      ) },
    { id: 'primary_email', label: 'Email', defaultWidth: 220,
      render: (person) => person.primary_email ? (
        <a
          href={`mailto:${person.primary_email}`}
          className="text-amber-600 hover:underline truncate block"
          onClick={(e) => e.stopPropagation()}
        >
          {person.primary_email}
        </a>
      ) : (
        <span className="text-slate-400">—</span>
      ) },
    { id: 'actions', label: 'Actions', defaultWidth: 150,
      render: (person) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {canCreateProposals && (
            <Button
              variant="ghost"
              size="sm"
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={() => handleNewProposal(person)}
              title="New Proposal"
            >
              <FileText className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewHistory(person)}
            title="View History"
          >
            <History className="w-4 h-4" />
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(person)}
              title="Edit"
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) },
  ];
  const contactsPrefs = useTablePreferences('people-contacts', contactColumns);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">
            Contacts
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {people.length} contact{people.length !== 1 ? 's' : ''} • Search by name, company, phone, or email
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-slate-800 text-white' : ''}
            >
              <TableIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-slate-800 text-white' : ''}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              data-testid="add-contact-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Contact
            </Button>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts by name, phone, email..."
            className="pl-10 bg-white"
            data-testid="search-contacts-input"
          />
        </div>
        <Select value={companyFilter || "all"} onValueChange={(v) => setCompanyFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-[200px] bg-white">
            <SelectValue placeholder="All Companies" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.company_name || c.client_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Contacts List */}
      {people.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">
              {search || companyFilter ? 'No contacts match your search' : 'No contacts yet'}
            </p>
            {canEdit && !search && !companyFilter && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { resetForm(); setShowDialog(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Contact
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View — resize, reorder, hide columns are per-user prefs */
        <Card className="border-slate-200">
          <div className="p-3">
            <PreferenceTable
              columns={contactColumns}
              rows={sortedPeople}
              rowKey={(p) => p.id}
              prefs={contactsPrefs}
              sortBy={sortBy}
              sortDir={sortOrder}
              onSort={(col) => handleSort(col.sortKey)}
              onRowClick={canEdit ? (person) => handleEdit(person) : undefined}
              rowClassName={() => canEdit ? 'cursor-pointer hover:bg-slate-50' : 'hover:bg-slate-50'}
              emptyMessage="No contacts match your filters."
              data-testid="people-contacts-table"
            />
          </div>
        </Card>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedPeople.map(person => (
            <Card 
              key={person.id} 
              className={canEdit ? "hover:shadow-md transition-shadow cursor-pointer" : "hover:shadow-md transition-shadow"}
              onClick={canEdit ? () => handleEdit(person) : undefined}
              data-testid={`contact-card-${person.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{person.full_name}</h3>
                      {person.job_title && (
                        <p className="text-sm text-slate-500">{person.job_title}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {person.company_name && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="truncate">{person.company_name}</span>
                  </div>
                )}
                
                {person.primary_phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <a 
                      href={`tel:${person.primary_phone}`}
                      className="text-amber-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {person.primary_phone}
                    </a>
                  </div>
                )}
                
                {person.primary_email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a 
                      href={`mailto:${person.primary_email}`}
                      className="text-amber-600 hover:underline truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {person.primary_email}
                    </a>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                  {canCreateProposals && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleNewProposal(person); }}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Proposal
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleViewHistory(person); }}
                    title="View History"
                  >
                    <History className="w-4 h-4" />
                  </Button>
                  {person.primary_phone && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${person.primary_phone}`; }}
                      className="text-green-600"
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      Call
                    </Button>
                  )}
                  {person.primary_email && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${person.primary_email}`; }}
                      className="text-blue-600"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Email
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="text-xl font-bold">
                {selectedPerson ? 'Edit Contact' : 'New Contact'}
              </DialogTitle>
              {selectedPerson && canCreateProposals && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => {
                    setShowDialog(false);
                    handleNewProposal(selectedPerson);
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  New Proposal
                </Button>
              )}
            </div>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company (Optional)</Label>
                <Select 
                  value={formData.company_id || "none"} 
                  onValueChange={(v) => {
                    if (v === "__new__") {
                      setFormData(prev => ({ ...prev, company_id: "__new__", company_name: "" }));
                    } else {
                      handleCompanyChange(v === "none" ? "" : v);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company or leave blank" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Company (Individual)</SelectItem>
                    <SelectItem value="__new__" className="text-amber-600">+ Create New Company</SelectItem>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name || c.client_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.company_id === "__new__" && (
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Enter new company name..."
                    className="mt-2 border-amber-300"
                    autoFocus
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                  placeholder="Project Manager"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                placeholder="Engineering, Permitting, etc."
              />
            </div>

            {/* Phone Numbers */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Phone Numbers</Label>
                <Button type="button" variant="outline" size="sm" onClick={addPhone}>
                  <Plus className="w-4 h-4 mr-1" />Add Phone
                </Button>
              </div>
              {formData.phones.map((phone, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={phone.number}
                    onChange={(e) => updatePhone(index, 'number', e.target.value)}
                    placeholder="(704) 555-1234"
                    className="flex-1"
                  />
                  <Select value={phone.type} onValueChange={(v) => updatePhone(index, 'type', v)}>
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant={phone.is_primary ? "default" : "outline"}
                    size="sm"
                    onClick={() => updatePhone(index, 'is_primary', true)}
                    className={phone.is_primary ? "bg-amber-500 text-white" : ""}
                  >
                    Primary
                  </Button>
                  {formData.phones.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removePhone(index)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Email Addresses */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Email Addresses</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEmail}>
                  <Plus className="w-4 h-4 mr-1" />Add Email
                </Button>
              </div>
              {formData.emails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="email"
                    value={email.email}
                    onChange={(e) => updateEmail(index, 'email', e.target.value)}
                    placeholder="john@company.com"
                    className="flex-1"
                  />
                  <Select value={email.type} onValueChange={(v) => updateEmail(index, 'type', v)}>
                    <SelectTrigger className="w-[110px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant={email.is_primary ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateEmail(index, 'is_primary', true)}
                    className={email.is_primary ? "bg-amber-500 text-white" : ""}
                  >
                    Primary
                  </Button>
                  {formData.emails.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEmail(index)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Addresses */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Addresses</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAddress}>
                  <Plus className="w-4 h-4 mr-1" />Add Address
                </Button>
              </div>
              {formData.addresses.map((addr, index) => (
                <Card key={index} className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Select value={addr.type} onValueChange={(v) => updateAddress(index, 'type', v)}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ADDRESS_TYPES.map(t => (
                          <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant={addr.is_primary ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateAddress(index, 'is_primary', true)}
                        className={addr.is_primary ? "bg-amber-500 text-white" : ""}
                      >
                        Primary
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeAddress(index)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={addr.street}
                      onChange={(e) => updateAddress(index, 'street', e.target.value)}
                      placeholder="Street address or PO Box"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={addr.city}
                        onChange={(e) => updateAddress(index, 'city', e.target.value)}
                        placeholder="City"
                        className="col-span-1"
                      />
                      <Input
                        value={addr.state}
                        onChange={(e) => updateAddress(index, 'state', e.target.value)}
                        placeholder="State"
                      />
                      <Input
                        value={addr.zip_code}
                        onChange={(e) => updateAddress(index, 'zip_code', e.target.value)}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </Card>
              ))}
              {formData.addresses.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-2">No addresses added</p>
              )}
            </div>

            {/* Preferred Contact Method */}
            <div className="space-y-2">
              <Label>Preferred Contact Method</Label>
              <Select 
                value={formData.preferred_contact_method} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, preferred_contact_method: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map(m => (
                    <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes about this contact..."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t">
              <div>
                {selectedPerson && canDelete && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => { setShowDialog(false); setShowDeleteDialog(true); }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {selectedPerson ? 'Update' : 'Create'} Contact
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Contact History - {selectedPerson?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto">
            {history.jobs.length === 0 && history.proposals.length === 0 && history.opportunities.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No history found for this contact</p>
            ) : (
              <>
                {history.jobs.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Jobs ({history.jobs.length})
                    </h4>
                    <div className="space-y-2">
                      {history.jobs.map(job => (
                        <Link
                          key={job.id}
                          to={`/jobs/${job.id}`}
                          className="block p-2 rounded-lg bg-slate-50 hover:bg-slate-100"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm text-amber-600">{job.job_number}</span>
                            <Badge className="text-xs">{job.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-700 truncate">{job.job_title}</p>
                          <p className="text-xs text-slate-500">{job.date}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                
                {history.proposals.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Proposals ({history.proposals.length})</h4>
                    <div className="space-y-2">
                      {history.proposals.map(proposal => (
                        <Link
                          key={proposal.id}
                          to={`/proposals?id=${proposal.id}`}
                          className="block p-2 rounded-lg bg-slate-50 hover:bg-slate-100"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm text-blue-600">{proposal.proposal_number}</span>
                            <Badge className="text-xs">{getStatusLabel(proposal.status)}</Badge>
                          </div>
                          <p className="text-sm text-slate-700 truncate">{proposal.title}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                
                {history.opportunities.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Opportunities ({history.opportunities.length})</h4>
                    <div className="space-y-2">
                      {history.opportunities.map(opp => (
                        <Link
                          key={opp.id}
                          to={`/opportunities?id=${opp.id}`}
                          className="block p-2 rounded-lg bg-slate-50 hover:bg-slate-100"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm text-purple-600">{opp.opportunity_number}</span>
                            <Badge className="text-xs">{opp.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-700 truncate">{opp.title}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPerson?.full_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
